import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { scannerApi, apiMessage, extractData } from 'shared/api/scanner'
import { useAuth } from 'context/AuthContext'
import { canManageStaff } from 'shared/util/permissions'
import { PermissionEmpty, EmptyState, StatusPill, Skeleton } from 'shared/components/ui/primitives'
import SearchBar from 'shared/components/searchbar'
import Button from 'shared/components/ui/button'
import { IconBack } from 'shared/components/icons'

function StaffList() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [searchItem, setSearchItem] = useState('')
  const [debounced, setDebounced] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const res = await scannerApi.staffList({ sSearch: debounced, nSkip: 0, nLimit: 50 })
      const data = extractData(res)
      setItems(data?.aScannerUsers || [])
    } catch (err) {
      toast.error(apiMessage(err))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [debounced])

  useEffect(() => {
    const id = setTimeout(() => setDebounced(searchItem), 400)
    return () => clearTimeout(id)
  }, [searchItem])

  useEffect(() => {
    if (canManageStaff(user)) load()
    else setLoading(false)
  }, [load, user])

  const deactivate = async (staff) => {
    if (!window.confirm(`Deactivate ${staff.sName}?`)) return
    try {
      await scannerApi.staffUpdate(staff._id, { eStatus: 'i' })
      toast.success('Staff deactivated')
      load()
    } catch (err) {
      toast.error(apiMessage(err))
    }
  }

  if (!canManageStaff(user)) return <PermissionEmpty />

  return (
    <div className="min-h-dvh bg-bg text-ink">
      <header className="sticky top-0 z-20 flex items-center gap-2 border-b border-line bg-surface px-3 py-3">
        <button type="button" className="flex h-12 w-12 items-center justify-center rounded-2xl bg-elevated" onClick={() => navigate(-1)}>
          <IconBack className="h-5 w-5" />
        </button>
        <h1 className="mb-0 flex-1 text-xl font-bold">Scanner staff</h1>
        <Button isSmall onClick={() => navigate('/staff/new')}>
          Add
        </Button>
      </header>
      <div className="mx-auto max-w-3xl px-4 py-4">
        <SearchBar searchItem={searchItem} onChangeFn={setSearchItem} placeHolder="Search staff" className="mb-4" />
        {loading
          ? (
          <Skeleton className="h-24" />
            )
          : items.length === 0
            ? (
          <EmptyState title="No staff yet" message="Add a scanner user and assign event permissions." />
              )
            : (
          <div className="space-y-3">
            {items.map((staff) => (
              <div key={staff._id} className="card-surface flex items-center gap-3 p-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-theme text-lg font-bold text-light">
                  {(staff.sName || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="mb-0 font-semibold">{staff.sName}</p>
                  <p className="mb-0 truncate text-xs text-muted">{staff.sEmail || `${staff.sCountryCode || ''} ${staff.sMobile || ''}`}</p>
                </div>
                <StatusPill status={staff.eStatus === 'a' ? 'active' : 'inactive'} />
                <button type="button" className="text-sm font-semibold text-theme" onClick={() => navigate(`/staff/${staff._id}`)}>
                  Edit
                </button>
                {staff.eStatus === 'a' && (
                  <button type="button" className="text-sm font-semibold text-danger" onClick={() => deactivate(staff)}>
                    Off
                  </button>
                )}
              </div>
            ))}
          </div>
              )}
      </div>
    </div>
  )
}

export default StaffList
